import React, { useState, useEffect } from 'react';

const SuperForm = ({ formData, handleChange, handleNumberWheel, handleSubmit, loading, createUserLoading, error, userProfileData, parentDetails, isEdit = false }) => {
  const inputClass = "w-full border border-gray-300 px-3 py-2 focus:outline-none focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
  const labelClass = "block text-gray-700 text-sm mb-1";
  const disabledInputClass = "w-full border border-gray-300 px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed";
  const errorInputClass = "w-full border-2 border-red-500 px-3 py-2 focus:outline-none focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({
    exposureLimit: '',
    myShare: '',
    casinoShare: '',
  });

  // Validate fields on change
  useEffect(() => {
    const errors = {};

    if (formData.exposureLimit && parseFloat(formData.exposureLimit) > (parentDetails?.availableBalance || 0)) {
      errors.exposureLimit = `Cannot exceed My Coins (${parentDetails?.availableBalance?.toFixed(2) || '0.00'})`;
    }

    if (formData.myShare && parseFloat(formData.myShare) > (parentDetails?.shares?.myShare || 0)) {
      errors.myShare = `Cannot exceed My Match Share (${parentDetails?.shares?.myShare?.toFixed(2) || '0.00'}%)`;
    }

    if (formData.casinoShare && parseFloat(formData.casinoShare) > (parentDetails?.shares?.casinoShare || 0)) {
      errors.casinoShare = `Cannot exceed My Casino Share (${parentDetails?.shares?.casinoShare?.toFixed(2) || '0.00'}%)`;
    }

    setValidationErrors(errors);
  }, [formData, parentDetails]);

  const hasValidationErrors = Object.values(validationErrors).some(Boolean);

  const handleFormSubmit = (event) => {
    if (!isEdit && hasValidationErrors) {
      event.preventDefault();
      return;
    }

    handleSubmit(event);
  };

  // Handler to limit input values to parent limits
  const handleLimitedChange = (event, maxValue) => {
    const { name, value } = event.target;
    
    // Allow empty input
    if (value === '') {
      handleChange(event);
      return;
    }

    const numValue = parseFloat(value);
    
    // If value exceeds max, set it to max
    if (!isNaN(numValue) && numValue > maxValue) {
      const limitedEvent = {
        ...event,
        target: {
          ...event.target,
          value: maxValue.toString()
        }
      };
      handleChange(limitedEvent);
    } else {
      handleChange(event);
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="px-4 py-4">
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mb-4">
        <div>
          <label className={labelClass}>
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter full name"
            className={inputClass}
            required
          />
        </div>

        {isEdit ? (
          <div>
            <label className={labelClass}>Status <span className="text-red-500">*</span></label>
            <select name="status" value={formData.status || 'active'} onChange={handleChange} className={inputClass}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        ) : (
          <>
            <div>
              <label className={labelClass}>My Coins</label>
              <input type="text" value={parentDetails?.availableBalance?.toFixed(2) || "0"} disabled className={disabledInputClass} />
            </div>
            <div>
              <label className={labelClass}>Coins <span className="text-red-500">*</span></label>
              <input type="number" name="exposureLimit" value={formData.exposureLimit} onChange={handleChange} onWheel={handleNumberWheel} placeholder="Super Coins" className={validationErrors.exposureLimit ? errorInputClass : inputClass} required />
              {validationErrors.exposureLimit && <p className="text-red-500 text-xs mt-1">{validationErrors.exposureLimit}</p>}
            </div>
          </>
        )}

        <div>
          <label className={labelClass}>Reference</label>
          <input
            type="text"
            name="reference"
            value={formData.reference || ''}
            onChange={handleChange}
            placeholder="Enter reference"
            className={inputClass}
          />
        </div>

        {/* <div>
          <label className={labelClass}>
            Share Type <span className="text-red-500">*</span>
          </label>
          <select
            name="shareType"
            value={formData.shareType || ""}
            onChange={handleChange}
            className={inputClass}
            required
          >
            <option value="">Select share type</option>
            <option value="fixed">fixed</option>
          </select>
        </div> */}
      </div>

      {/* Match Share and Commission */}
      <h1 className="text-lg font-semibold mb-3 mt-6">
        Super Match Share and Comm
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mb-4">
        <div>
          <label className={labelClass}>My Match Share(%)</label>
          <input
            type="text"
            value={parentDetails?.shares?.myShare?.toFixed(2) || "0"}
            disabled
            className={disabledInputClass}
          />
        </div>

        <div>
          <label className={labelClass}>
            Match Share(%) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            name="myShare"
            value={formData.myShare}
            onChange={handleChange}
            onWheel={handleNumberWheel}
            placeholder="Super Match Share"
            className={validationErrors.myShare ? errorInputClass : inputClass}
            required
          />
          {validationErrors.myShare && (
            <p className="text-red-500 text-xs mt-1">{validationErrors.myShare}</p>
          )}
        </div>

        <div>
          <label className={labelClass}>
            My Comm type
          </label>
          <input
            type="text"
            value="Bet by bet"
            disabled
            className={disabledInputClass}
          />
        </div>

        <div>
          <label className={labelClass}>
            Comm type <span className="text-red-500">*</span>
          </label>
          <select
            name="commType"
            value={formData.commType || "No Comm"}
            onChange={handleChange}
            className={inputClass}
            required
          >
            <option value="No Comm">No Comm</option>
            <option value="Bet by bet">Bet by bet</option>
          </select>
        </div>

        {/* Show additional commission fields when Bet by bet is selected */}
        {formData.commType === "Bet by bet" && (
          <>
            <div>
              <label className={labelClass}>
                My Match comm(%)
              </label>
              <input
                type="text"
                value={parentDetails?.shares?.matchCommission?.toFixed(2) || "0"}
                disabled
                className={disabledInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                Match comm(%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                name="matchCommission"
                value={formData.matchCommission}
                onChange={handleChange}
                onWheel={handleNumberWheel}
                placeholder="Match commission"
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className={labelClass}>
                My Sess comm(%)
              </label>
              <input
                type="text"
                value={parentDetails?.shares?.sessionCommission?.toFixed(2) || "0"}
                disabled
                className={disabledInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                Sess comm(%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                name="sessionCommission"
                value={formData.sessionCommission}
                onChange={handleChange}
                onWheel={handleNumberWheel}
                placeholder="session commission"
                className={inputClass}
                required
              />
            </div>
          </>
        )}
      </div>

      {/* Casino & Matka Share and Commission */}
      <h1 className="text-lg font-semibold mb-3 mt-6">
        Super Casino Commission
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mb-6">
        {/* <div>
          <label className={labelClass}>My Casino Share(%)</label>
          <input
            type="text"
            value={parentDetails?.shares?.casinoShare?.toFixed(2) || "0"}
            disabled
            className={disabledInputClass}
          />
        </div>

        <div>
          <label className={labelClass}>
            Casino Share(%) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            name="casinoShare"
            value={formData.casinoShare}
            onChange={handleChange}
            onWheel={handleNumberWheel}
            placeholder="Super Casino Share"
            className={validationErrors.casinoShare ? errorInputClass : inputClass}
            required
          />
          {validationErrors.casinoShare && (
            <p className="text-red-500 text-xs mt-1">{validationErrors.casinoShare}</p>
          )}
        </div> */}

            <div>
              <label className={labelClass}>
                My Casino comm(%)
              </label>
              <input
                type="text"
                value={parentDetails?.shares?.casinoCommission?.toFixed(2) || "0"}
                disabled
                className={disabledInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                Casino comm(%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="casinoCommission"
                value={formData.casinoCommission}
                onChange={handleChange}
                onWheel={handleNumberWheel}
                placeholder="Casino commission"
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>
                My Matka comm(%)
              </label>
              <input
                type="text"
                value={parentDetails?.shares?.matkaCommission?.toFixed(2) || "0"}
                disabled
                className={disabledInputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                Matka comm(%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="matkaCommission"
                value={formData.matkaCommission}
                onChange={handleChange}
                onWheel={handleNumberWheel}
                placeholder="Matka commission"
                className={inputClass}
                required
              />
            </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading || createUserLoading || (!isEdit && hasValidationErrors)}
          className="bg-blue-500 text-white px-8 py-2 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
        >
          {loading || createUserLoading ? "Creating..." : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default SuperForm;